import { z } from 'zod';
import { dateStr, objectIdStr } from './common.js';
import { stationKey } from './station.js';
import { batch, job } from './job.js';
import { booking } from './booking.js';
import { part } from './part.js';
import { scan } from './scan.js';

/**
 * The engine reports rather than throws (SPEC §3): the plan is still written and the
 * warning surfaces as a badge.
 */
export const WARNING_CODES = [
  'zeroCapacity',
  'horizonExhausted',
  'pastDelivery',
  'noHours',
  'routingCycle',
] as const;
export type WarningCode = (typeof WARNING_CODES)[number];

export const warning = z.object({
  code: z.enum(WARNING_CODES),
  stationKey: stationKey.optional(),
  message: z.string(),
});
export type Warning = z.infer<typeof warning>;

/** Badges derived from plan vs actual (SPEC §4). Never derived from *absent* scans. */
export const BADGE_CODES = [
  'overrunning',
  'notStarted',
  'confirmedSlippage',
  'atRisk',
] as const;
export type BadgeCode = (typeof BADGE_CODES)[number];

export const badge = z.object({
  code: z.enum(BADGE_CODES),
  batchId: objectIdStr,
  stationKey: stationKey.optional(),
  message: z.string(),
});
export type Badge = z.infer<typeof badge>;

/** Per-station plan derived from that batch's bookings. */
export const stepPlan = z.object({
  stationKey,
  hoursPlanned: z.number(),
  hoursBooked: z.number(),
  start: dateStr.optional(),
  finish: dateStr.optional(),
  pinned: z.boolean(),
  /**
   * Positive-only evidence: how many *tracked* parts were scanned at this station or
   * a later one. Silence means nothing (SPEC §4).
   */
  partsConfirmed: z.number(),
  lastScanAt: z.coerce.date().optional(),
  /** A scan at this station dated after its planned finish. */
  slippedAt: z.coerce.date().optional(),
});
export type StepPlan = z.infer<typeof stepPlan>;

export const batchView = batch.extend({
  plan: z.array(stepPlan),
  finishDate: dateStr.optional(),
  warnings: z.array(warning).default([]),
  badges: z.array(badge).default([]),
  partsTotal: z.number(),
  partsTracked: z.number(),
});
export type BatchView = z.infer<typeof batchView>;

/** The list carries a note/attachment *count* only — the notes and attachments
 * themselves would bloat every row. */
export const jobSummary = job.omit({ notes: true, attachments: true }).extend({
  noteCount: z.number().default(0),
  attachmentCount: z.number().default(0),
  /** Not-done rows on the job's List page — what still needs doing. */
  openListItemCount: z.number().default(0),
  batchCount: z.number(),
  /** Set only for the common single-batch job, so the list can link straight to its
   * parts page without the UI having to pick among several batches. */
  soleBatchId: objectIdStr.optional(),
  dateStart: dateStr.optional(),
  dateDelivery: dateStr.optional(),
  finishDate: dateStr.optional(),
  partsTotal: z.number(),
  partsTracked: z.number(),
  badges: z.array(badge).default([]),
});
export type JobSummary = z.infer<typeof jobSummary>;

export const jobDetail = job.extend({
  batches: z.array(batchView),
  badges: z.array(badge).default([]),
});
export type JobDetail = z.infer<typeof jobDetail>;

/** One chip on the board: a batch's hours at one station on one day. */
export const boardChip = booking.extend({
  jobName: z.string(),
  batchName: z.string(),
  /** `Smith` for single-batch jobs, `Smith · 2nd floor` otherwise. */
  label: z.string(),
  /** Highlights the label, so one job is followable across stations and weeks. */
  jobColor: z.string(),
});
export type BoardChip = z.infer<typeof boardChip>;

export const boardDay = z.object({
  date: dateStr,
  isWorkday: z.boolean(),
});
export type BoardDay = z.infer<typeof boardDay>;

export const boardCell = z.object({
  stationKey,
  date: dateStr,
  capacityHours: z.number(),
  bookedHours: z.number(),
  chips: z.array(boardChip),
});
export type BoardCell = z.infer<typeof boardCell>;

export const boardResponse = z.object({
  from: dateStr,
  days: z.array(boardDay),
  cells: z.array(boardCell),
});
export type BoardResponse = z.infer<typeof boardResponse>;

export const scheduleResult = z.object({
  batchId: objectIdStr,
  finishDate: dateStr.optional(),
  bookings: z.number(),
  warnings: z.array(warning),
});
export type ScheduleResult = z.infer<typeof scheduleResult>;

/**
 * What one board drag did. `moved: false` is a normal, expected outcome — a step that
 * is already as early as capacity allows cannot be dragged earlier, and saying so is
 * the point: the board offers priority or a pin instead. A move that changes nothing
 * undoes itself, so it never leaves a dead constraint behind.
 */
export const planMoveResult = z.object({
  moved: z.boolean(),
  stationKey,
  /** Where the moved part of the step started, before and after. */
  from: dateStr,
  to: dateStr.optional(),
  /** Present when `moved` is false: why the plan did not change. */
  reason: z.string().optional(),
  /**
   * Days of this step held in place because the chip grabbed was not its first — those
   * bookings were pinned, and undo unpins them again.
   */
  heldDays: z.number().default(0),
  result: scheduleResult.optional(),
  /** How many moves are on this batch's undo stack now. */
  undoDepth: z.number().default(0),
});
export type PlanMoveResult = z.infer<typeof planMoveResult>;

export const partsResponse = z.object({
  parts: z.array(part),
  total: z.number(),
  tracked: z.number(),
});
export type PartsResponse = z.infer<typeof partsResponse>;

export const labelSheet = z.object({
  jobName: z.string(),
  batchName: z.string(),
  labels: z.array(
    z.object({
      qrText: z.string(),
      partCode: z.string(),
      name: z.string().optional(),
      componentCode: z.string().optional(),
    }),
  ),
  /** Honest coverage: "N of M parts tracked". */
  total: z.number(),
  tracked: z.number(),
});
export type LabelSheet = z.infer<typeof labelSheet>;

/**
 * Scans with the names a reader needs, resolved server-side: a scan stores ids, and a
 * bare part code answers "which cabinet was that?" only for whoever printed the label.
 * One shape for both readers — a job's own history and the shop-wide Scans page, which
 * is the only one that needs `jobName`.
 */
export const scanRow = scan.extend({
  partCode: z.string().optional(),
  partName: z.string().optional(),
  batchName: z.string().optional(),
  jobName: z.string().optional(),
});
export type ScanRow = z.infer<typeof scanRow>;

export const scansResponse = z.object({ scans: z.array(scanRow) });
export type ScansResponse = z.infer<typeof scansResponse>;
